import { Component } from '@theme/component';
import { ProductDetailsSheetToggleEvent } from '@theme/events';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} handle - The grab handle that moves the sheet between peek and expanded.
 * @property {HTMLElement} expandedSnap - Snap marker whose scroll margin is the strip height.
 */

/**
 * Mobile product page "sheet" driven by the page's own scroll.
 *
 * The media gallery is pinned to the viewport. Scrolling the page moves the details (and every
 * section and the footer after them) up over the gallery until the edge reaches the strip left
 * by `--sheet-height`; from there the `pinned` attribute lifts the gallery above the content,
 * clipped to the strip, and content slides under it. Nothing is moved into the sheet.
 *
 * Layout lives in the block stylesheet. The sheet edge itself is never synced from script, so
 * it moves with the browser's own scrolling. This component measures the space above the sheet
 * and publishes positions on the root element, which section re-renders never replace:
 * `--product-sheet-header` (header group bottom in the document) and
 * `--product-sheet-gallery-top` (gallery top in the viewport).
 *
 * @extends {Component<Refs>}
 */
export class ProductDetailsSheet extends Component {
  requiredRefs = ['handle', 'expandedSnap'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {number | null} */
  #measureFrame = null;

  /** Viewport offset in px where the sheet edge stops and content starts sliding under the gallery */
  #stripHeight = 0;

  /** Document offset in px of the header group's bottom edge */
  #headerBottom = 0;

  #expanded = false;

  /**
   * Whether the sheet layout is in effect: mobile, with media pinned behind it.
   */
  get active() {
    return Boolean(this.#controller) && !mediaQueryLarge.matches;
  }

  /**
   * Whether the sheet is open, with its edge at the strip.
   */
  get expanded() {
    return this.active && this.#expanded;
  }

  connectedCallback() {
    super.connectedCallback();

    // Without media there is nothing to pin, and the stylesheet leaves the details in normal flow
    if (!this.#hasMedia) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    window.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#requestMeasure, { signal });
    mediaQueryLarge.addEventListener('change', this.#measure, { signal });

    // Content above the sheet (header, announcement bar, section padding) can change height after load
    this.#resizeObserver = new ResizeObserver(this.#requestMeasure);
    this.#resizeObserver.observe(document.documentElement);

    this.#measure();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    if (this.#measureFrame !== null) cancelAnimationFrame(this.#measureFrame);
    this.#measureFrame = null;

    const rootStyle = document.documentElement.style;
    rootStyle.removeProperty('--product-sheet-gallery-top');
    rootStyle.removeProperty('--product-sheet-header');
  }

  /**
   * Moves the sheet between its peek and expanded positions.
   */
  handleClick() {
    if (!this.#hasMedia || mediaQueryLarge.matches) return;

    const expandedScrollTop = this.getBoundingClientRect().top + window.scrollY - this.#stripHeight;
    const isExpanded = window.scrollY >= expandedScrollTop - 1;

    window.scrollTo({
      top: isExpanded ? 0 : expandedScrollTop,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  get #hasMedia() {
    return Boolean(this.parentElement?.querySelector(':scope > .product-information__media'));
  }

  /**
   * Positions the sheet so its edge starts `--sheet-peek` above the bottom of the first screen,
   * whatever renders above the product section.
   */
  #measure = () => {
    if (!this.#controller || mediaQueryLarge.matches) return;

    const margin = parseFloat(getComputedStyle(this).marginBlockStart) || 0;
    const above = `${Math.round(this.getBoundingClientRect().top + window.scrollY - margin)}px`;

    if (this.style.getPropertyValue('--sheet-above') !== above) this.style.setProperty('--sheet-above', above);

    // Where the header group ends in the document. `#header-group` itself can't be measured: with a
    // sticky header it is `display: contents` (no box), and its sticky child moves while scrolling.
    // The main content starts right after it and is never sticky.
    const main = document.getElementById('MainContent');
    this.#headerBottom = main ? Math.round(main.getBoundingClientRect().top + window.scrollY) : 0;
    this.#stripHeight = parseFloat(getComputedStyle(this.refs.expandedSnap).scrollMarginBlockStart) || 0;

    const rootStyle = document.documentElement.style;
    const header = `${this.#headerBottom}px`;
    if (rootStyle.getPropertyValue('--product-sheet-header') !== header) {
      rootStyle.setProperty('--product-sheet-header', header);
    }

    this.#update();
  };

  /** Batches resize observations (lazy media, fonts) into one measurement per frame. */
  #requestMeasure = () => {
    if (this.#measureFrame === null) {
      this.#measureFrame = requestAnimationFrame(() => {
        this.#measureFrame = null;
        this.#measure();
      });
    }
  };

  #requestUpdate = () => {
    if (this.#frame === null) this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * Feeds the sheet edge position to the gallery clip and syncs the handle state.
   */
  #update = () => {
    this.#frame = null;

    // Read everything before writing so the frame never forces a synchronous layout
    const top = this.getBoundingClientRect().top;
    const galleryTop = `${this.#headerVisibleBottom()}px`;
    const expanded = top <= this.#stripHeight + 1;

    // Only write on change: a custom property on the root restyles the whole document
    const rootStyle = document.documentElement.style;
    if (rootStyle.getPropertyValue('--product-sheet-gallery-top') !== galleryTop) {
      rootStyle.setProperty('--product-sheet-gallery-top', galleryTop);
    }

    // Section re-renders reset these, so compare against the DOM rather than cached state
    const { handle } = this.refs;
    if (handle.getAttribute('aria-expanded') !== `${expanded}`) handle.setAttribute('aria-expanded', `${expanded}`);

    // Keep the handle at the strip while the rest of the page scrolls under it, and switch the
    // gallery above the content, clipped to the strip
    if (this.hasAttribute('pinned') !== expanded) this.toggleAttribute('pinned', expanded);

    if (expanded !== this.#expanded) {
      this.#expanded = expanded;
      this.dispatchEvent(new ProductDetailsSheetToggleEvent(expanded));
    }
  };

  /**
   * The lowest visible edge of the header group in the viewport, so the gallery sits below
   * whatever part of it is on screen: the whole group at the top of the page, then a sticky
   * header alone, or nothing once a non-sticky or scroll-up header has left the viewport.
   * @returns {number}
   */
  #headerVisibleBottom() {
    const headerGroup = document.getElementById('header-group');
    if (!headerGroup) return Math.max(0, this.#headerBottom - Math.round(window.scrollY));

    let bottom = 0;
    for (const section of headerGroup.children) {
      bottom = Math.max(bottom, section.getBoundingClientRect().bottom);
    }

    return Math.round(bottom);
  }
}

if (!customElements.get('product-details-sheet')) {
  customElements.define('product-details-sheet', ProductDetailsSheet);
}
